import {IssueCreatePage} from '@github-ui/issue-create/IssueCreatePage'
import type {IssueCreateDiscussionData, IssueCreateMetadataTypes} from '@github-ui/issue-create/TemplateArgs'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {SimilarIssueSidePanel} from '../../components/SimilarIssueSidePanel'
import {VALUES} from '../../constants/values'
import type {AppPayload} from '../../types/app-payload'
import type {InternalIssueNewPageUrlArgumentsMetadata$key} from './__generated__/InternalIssueNewPageUrlArgumentsMetadata.graphql'
import type {InternalIssueNewPageUrlArgumentsMetadataQuery} from './__generated__/InternalIssueNewPageUrlArgumentsMetadataQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const InternalIssueNewPageUrlArgumentsMetadata = graphql`
  query InternalIssueNewPageUrlArgumentsMetadataQuery(
    $owner: String!
    $name: String!
    $withAssignees: Boolean = false
    $assigneeLogins: String = ""
    $withLabels: Boolean = false
    $labelNames: String = ""
    $withMilestone: Boolean = false
    $milestoneTitle: String = ""
    $type: String = ""
    $withType: Boolean = false
    $withProjects: Boolean = false
    $projectNumbers: [Int!] = []
    $withTriagePermission: Boolean = false
    $discussionNumber: Int = 0
    $includeDiscussion: Boolean = false
    $templateFilter: String = ""
    $withTemplate: Boolean = false
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...InternalIssueNewPageUrlArgumentsMetadata
        @arguments(
          withAssignees: $withAssignees
          assigneeLogins: $assigneeLogins
          withLabels: $withLabels
          labelNames: $labelNames
          withMilestone: $withMilestone
          milestoneTitle: $milestoneTitle
          type: $type
          withType: $withType
          withProjects: $withProjects
          projectNumbers: $projectNumbers
          withTriagePermission: $withTriagePermission
          discussionNumber: $discussionNumber
          includeDiscussion: $includeDiscussion
          templateFilter: $templateFilter
          withTemplate: $withTemplate
        )
    }
  }
`

export type InternalIssueNewPageProps = {
  urlParameterQueryData: PreloadedQuery<InternalIssueNewPageUrlArgumentsMetadataQuery>
}

export const InternalIssueNewPageWithUrlParams = ({urlParameterQueryData}: InternalIssueNewPageProps) => {
  const repositoryData = usePreloadedQuery<InternalIssueNewPageUrlArgumentsMetadataQuery>(
    InternalIssueNewPageUrlArgumentsMetadata,
    urlParameterQueryData,
  )?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <InternalIssueNewPageWithUrlParamsInternal repository={okResult.value} />
}

const InternalIssueNewPageWithUrlParamsInternal = ({
  repository,
}: {
  repository: InternalIssueNewPageUrlArgumentsMetadata$key
}) => {
  const appPayload = useAppPayload<AppPayload>()

  const repositoryData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment InternalIssueNewPageUrlArgumentsMetadata on Repository
      @argumentDefinitions(
        withAssignees: {type: "Boolean!", defaultValue: false}
        assigneeLogins: {type: "String!", defaultValue: ""}
        withLabels: {type: "Boolean!", defaultValue: false}
        labelNames: {type: "String!", defaultValue: ""}
        withMilestone: {type: "Boolean!", defaultValue: false}
        milestoneTitle: {type: "String!", defaultValue: ""}
        type: {type: "String!", defaultValue: ""}
        withType: {type: "Boolean!", defaultValue: false}
        withProjects: {type: "Boolean!", defaultValue: false}
        projectNumbers: {type: "[Int!]!", defaultValue: []}
        withTriagePermission: {type: "Boolean!", defaultValue: false}
        discussionNumber: {type: "Int", defaultValue: 0}
        includeDiscussion: {type: "Boolean!", defaultValue: false}
        templateFilter: {type: "String!", defaultValue: ""}
        withTemplate: {type: "Boolean!", defaultValue: false}
      ) {
        ...IssueCreatePage @arguments(templateFilter: $templateFilter, withTemplate: $withTemplate)
        viewerIssueCreationPermissions {
          assignable @include(if: $withAssignees)
          labelable @include(if: $withLabels)
          milestoneable @include(if: $withMilestone)
          triageable @include(if: $withTriagePermission)
          typeable @include(if: $withType)
        }
        suggestedActors(capabilities: [CAN_BE_ASSIGNED], first: 10, loginNames: $assigneeLogins)
          @include(if: $withAssignees) {
          nodes {
            ... on Actor {
              __typename
              id
              login
              name
              profileResourcePath
              avatarUrl(size: 64)
              ... on Bot {
                isCopilot
                isAgent
                displayName
              }
            }
          }
        }
        labels(first: 20, names: $labelNames) @include(if: $withLabels) {
          nodes {
            id
            color
            name
            nameHTML
            description
            url
          }
        }
        discussion(number: $discussionNumber) @include(if: $includeDiscussion) {
          formattedBody
          title
          labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
            edges {
              node {
                id
                color
                name
                nameHTML
                description
                url
              }
            }
          }
        }
        milestoneByTitle(title: $milestoneTitle) @include(if: $withMilestone) {
          id
          title
          closed
          dueOn
          progressPercentage
          url
          closedAt
        }
        issueType(name: $type) @include(if: $withType) {
          id
          name
          isEnabled
          description
          color
          pinnedFields {
            __typename
            ... on IssueFieldText {
              id
              name
              dataType
            }
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
            }
            ... on IssueFieldDate {
              id
              name
              dataType
            }
            ... on IssueFieldNumber {
              id
              name
              dataType
            }
          }
        }
        owner {
          ... on ProjectV2Owner {
            projectsV2ByNumber(first: 20, numbers: $projectNumbers) @include(if: $withProjects) {
              nodes {
                id
                title
                closed
                number
                url
                viewerCanUpdate
                hasReachedItemsLimit
                __typename
              }
            }
          }
        }
      }
    `,
    repository,
  )

  const assigneeNodes = repositoryData?.suggestedActors?.nodes
  const assignees =
    assigneeNodes && repositoryData.viewerIssueCreationPermissions?.assignable
      ? assigneeNodes.flatMap(e => (e ? [e] : []))
      : undefined

  const labelNodes = repositoryData?.labels?.nodes
  const labels =
    labelNodes && repositoryData.viewerIssueCreationPermissions?.labelable
      ? labelNodes.flatMap(e => (e ? [e] : []))
      : undefined

  const projectNodes = repositoryData?.owner?.projectsV2ByNumber?.nodes
  const projects =
    projectNodes && repositoryData.viewerIssueCreationPermissions?.triageable
      ? projectNodes.filter(e => !!e)
      : undefined

  const milestone =
    repositoryData?.milestoneByTitle && repositoryData.viewerIssueCreationPermissions?.milestoneable
      ? repositoryData?.milestoneByTitle
      : undefined

  const issueType =
    repositoryData?.issueType &&
    repositoryData.viewerIssueCreationPermissions?.triageable &&
    repositoryData.viewerIssueCreationPermissions?.typeable
      ? repositoryData?.issueType
      : undefined

  const discussion = repositoryData?.discussion
    ? ({
        title: repositoryData.discussion.title,
        formattedBody: repositoryData.discussion.formattedBody,
        labels: repositoryData.discussion.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])),
      } as IssueCreateDiscussionData)
    : undefined

  const initialMetadataValues: IssueCreateMetadataTypes = {
    ...(assignees !== undefined && {assignees}),
    ...(labels !== undefined && {labels}),
    ...(milestone !== undefined && {milestone}),
    ...(projects !== undefined && {projects}),
    ...(issueType !== undefined && {type: issueType}),
    ...(discussion !== undefined && {discussion}),
  }
  const storageKeyPrefix = VALUES.storageKeyPrefix(appPayload)
  const emojiSkinTonePreference = appPayload?.current_user_settings?.preferred_emoji_skin_tone
  const copilotShowFunctionality = appPayload?.current_user_settings?.copilot_show_functionality || false

  return (
    <SimilarIssueSidePanel repository={appPayload.scoped_repository}>
      {onSimilarIssueClick => (
        <IssueCreatePage
          initialMetadataValues={initialMetadataValues}
          storageKeyPrefix={storageKeyPrefix}
          emojiSkinTonePreference={emojiSkinTonePreference}
          copilotShowFunctionality={copilotShowFunctionality}
          currentRepository={repositoryData}
          onSimilarIssueClick={onSimilarIssueClick}
        />
      )}
    </SimilarIssueSidePanel>
  )
}
